/**
 * Reports > Engagement > Survey Results (TRUS-624).
 *
 * Every form the org shares by public link, with live per-question results,
 * the recent written answers, CSV export, and the links to hand out: the
 * survey itself and its public results page. Built for Latinas Contra Cancer's
 * Appointment Justice survey, but nothing here is LCC-specific — any org's
 * public-link form shows up.
 *
 * The optional contact section of an anonymous survey is never here: the
 * server leaves identity fields out of the summary and the CSV. People who
 * asked to be contacted are in Contacts, with no link back to their answers.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, Loader2, ClipboardList, Copy, Check, ExternalLink, AlertTriangle } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useDynamicTranslation } from '@/components/forms/dynamicTranslation';
import SurveyResultsView, { type SurveyResultsData } from '@/components/forms/SurveyResultsView';

const REFRESH_MS = 30_000;

interface SurveyLink {
  id: number;
  token: string;
  title?: string | null;
  is_active: boolean;
  anonymous_responses: boolean;
  public_results_enabled: boolean;
  current_submissions: number;
  public_url: string;
  results_url: string | null;
}

interface SurveyListItem {
  template_id: number;
  name: string;
  is_active: boolean;
  total_responses: number;
  last_response_at: string | null;
  links: SurveyLink[];
}

const CopyLink: React.FC<{ label: string; path: string }> = ({ label, path }) => {
  const { t } = useAppTranslation();
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${path}`;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-28 shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
      <code className="flex-1 truncate rounded bg-gray-50 dark:bg-gray-900 px-2 py-1 text-xs text-gray-700 dark:text-gray-300">
        {url}
      </code>
      <button
        type="button"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          } catch {
            /* clipboard blocked; the URL is visible to copy by hand */
          }
        }}
        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label={t('survey_results.copy_link', 'Copy link')}
        title={t('survey_results.copy_link', 'Copy link')}
      >
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label={t('survey_results.open_link', 'Open in a new tab')}
        title={t('survey_results.open_link', 'Open in a new tab')}
      >
        <ExternalLink className="w-4 h-4 text-gray-500" />
      </a>
    </div>
  );
};

export const SurveyResultsReport: React.FC = () => {
  const { t, language } = useAppTranslation();
  const [bundle, setBundle] = useState<Record<string, string> | null>(null);
  const tr = useDynamicTranslation(bundle);
  const [surveys, setSurveys] = useState<SurveyListItem[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [summary, setSummary] = useState<(SurveyResultsData & { name: string }) | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const loadSurveys = useCallback(async () => {
    try {
      const resp = await apiClient.get<SurveyListItem[]>('/api/forms/survey-results');
      setSurveys(resp.data);
      setSelectedId((current) => current ?? resp.data[0]?.template_id ?? null);
    } catch {
      setSurveys([]);
      setError(t('survey_results.list_failed', 'Could not load surveys.'));
    }
  }, [t]);

  const loadSummary = useCallback(
    async (id: number, quiet = false) => {
      if (!quiet) setLoading(true);
      try {
        const resp = await apiClient.get<SurveyResultsData & { name: string }>(
          `/api/forms/templates/${id}/response-summary`,
        );
        setSummary(resp.data);
        setUpdatedAt(new Date());
        setError(null);
      } catch {
        setError(t('survey_results.load_failed', 'Could not load results'));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadSurveys();
  }, [loadSurveys]);

  useEffect(() => {
    if (selectedId != null) void loadSummary(selectedId);
  }, [selectedId, loadSummary]);

  useEffect(() => {
    if (!autoRefresh || selectedId == null) return undefined;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadSummary(selectedId, true);
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, selectedId, loadSummary]);

  // Question and option labels are tenant content: a Spanish-speaking staff
  // member reads them through the org's own translations, as on the survey.
  useEffect(() => {
    if (language === 'en') {
      setBundle(null);
      return undefined;
    }
    let cancelled = false;
    apiClient
      .get<{ translations: Record<string, string> }>(`/api/translations/dynamic/${language}`)
      .then((resp) => {
        if (!cancelled) setBundle(resp.data?.translations || null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [language]);

  const selected = useMemo(
    () => surveys?.find((s) => s.template_id === selectedId) || null,
    [surveys, selectedId],
  );
  const primaryLink = selected?.links.find((l) => l.is_active) || selected?.links[0] || null;

  const exportCsv = async () => {
    if (selectedId == null) return;
    setExporting(true);
    try {
      const resp = await apiClient.get(`/api/forms/templates/${selectedId}/responses.csv`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(selected?.name || 'survey').replace(/[^A-Za-z0-9]+/g, '_')}_responses.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError(t('survey_results.export_failed', 'Could not export responses.'));
    } finally {
      setExporting(false);
    }
  };

  const setPublicResults = async (link: SurveyLink, enabled: boolean) => {
    try {
      await apiClient.patch(`/api/forms/public-tokens/${link.id}`, { public_results_enabled: enabled });
      await loadSurveys();
    } catch {
      setError(t('survey_results.update_failed', 'Could not update the link.'));
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" aria-hidden="true" />
            {t('survey_results.title', 'Survey Results')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t(
              'survey_results.subtitle',
              'Live results for forms shared by public link. Contact details people chose to leave are in Contacts, never here.',
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            {t('survey_results.auto_refresh', 'Auto-refresh')}
          </label>
          <button
            type="button"
            onClick={() => selectedId != null && void loadSummary(selectedId)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {t('survey_results.refresh', 'Refresh')}
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={selectedId == null || exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {t('survey_results.export_csv', 'Export CSV')}
          </button>
        </div>
      </header>

      {surveys === null ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : surveys.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500">
          {t(
            'survey_results.empty',
            'No forms are shared by public link yet. Create a public link from the Form Builder to collect responses.',
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="survey-results-picker" className="text-sm text-gray-600 dark:text-gray-400">
              {t('survey_results.survey', 'Survey')}
            </label>
            <select
              id="survey-results-picker"
              value={selectedId ?? ''}
              onChange={(e) => setSelectedId(Number(e.target.value))}
              className="min-w-[18rem] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
            >
              {surveys.map((s) => (
                <option key={s.template_id} value={s.template_id}>
                  {s.name} ({s.total_responses})
                </option>
              ))}
            </select>
            {updatedAt && (
              <span className="text-xs text-gray-400">
                {t('survey_results.updated_at', 'Updated')} {updatedAt.toLocaleTimeString()}
              </span>
            )}
          </div>

          {primaryLink && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-2">
              <CopyLink label={t('survey_results.survey_link', 'Survey link')} path={primaryLink.public_url} />
              {primaryLink.results_url ? (
                <CopyLink label={t('survey_results.results_link', 'Results link')} path={primaryLink.results_url} />
              ) : null}
              <label className="flex items-center gap-2 pt-1 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={primaryLink.public_results_enabled}
                  onChange={(e) => void setPublicResults(primaryLink, e.target.checked)}
                />
                {t('survey_results.public_results_toggle', 'Anyone with the results link can see live totals')}
              </label>
              {primaryLink.anonymous_responses && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('survey_results.anonymous_link', 'Anonymous link: responses carry no name, email or phone.')}
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <AlertTriangle className="w-4 h-4" aria-hidden="true" />
              {error}
            </div>
          )}

          {loading && !summary ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : summary ? (
            <SurveyResultsView data={summary} tr={tr} showTextAnswers />
          ) : null}
        </>
      )}
    </div>
  );
};

export default SurveyResultsReport;
